import React, { FormEvent, useState } from 'react';
import { Link, RouteComponentProps } from 'react-router-dom';

import AuthCard from '@/features/auth/components/auth-card/AuthCard';
import GoogleButton from '@/features/auth/components/google-button/GoogleButton';
import { carryRedirect, safeRedirect } from '@/features/auth/safeRedirect';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Login(props: RouteComponentProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // ?redirect=/apps comes from the auth-guard bouncing us here — or from
  // /cli-login, which must get the person back to approve their CLI.
  const redirectTo = safeRedirect(props.location.search);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!EMAIL_RE.test(email)) {
      setError('Enter a valid email address.');
      return;
    }
    if (!password) {
      setError('Password is required.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ email, password }),
      });
      const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
      if (!res.ok) {
        setError((data.error as string) || 'Login failed.');
        return;
      }
      // Backend set the cookie; navigate to the original target.
      window.location.assign(redirectTo);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Network error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthCard title="Welcome back" subtitle="Sign in to continue building.">
      {error ? <div className="auth-banner auth-banner-error">{error}</div> : null}
      {/* Renders nothing at all unless tile-auth reports Google as configured. */}
      <GoogleButton redirectTo={redirectTo} onError={setError} text="signin_with" />
      <form onSubmit={onSubmit} noValidate>
        <label className="auth-label" htmlFor="email">
          Email
        </label>
        <input
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          placeholder="you@company.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="auth-input"
          required
        />
        <label className="auth-label" htmlFor="password">
          Password
        </label>
        <input
          id="password"
          name="password"
          type="password"
          autoComplete="current-password"
          placeholder="Your password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="auth-input"
          required
        />
        <button type="submit" disabled={submitting} className="auth-btn auth-btn-primary">
          {submitting ? 'Signing in…' : 'Sign in'}
        </button>
      </form>

      <p className="auth-foot">
        Don&apos;t have an account?{' '}
        <Link to={`/register${carryRedirect(props.location.search)}`} className="auth-link">
          Create one
        </Link>
      </p>
    </AuthCard>
  );
}
